import html2canvas from "html2canvas";
import { saveAs } from 'file-saver';
import { authHeader } from "../../../javascript/auth-header";
import { getVintageFromReportType } from "../../Common/Helpers";

export function waitForPaint(): Promise<void> {
    return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
}

export function waitForElement(selector: string, timeoutMs = 8000): Promise<Element> {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(selector);
        if (existing) {
            resolve(existing);
            return;
        }

        const start = Date.now();
        const tick = () => {
            const el = document.querySelector(selector);
            if (el) {
                resolve(el);
                return;
            }
            if (Date.now() - start >= timeoutMs) {
                reject(new Error(`Timed out waiting for ${selector}`));
                return;
            }
            setTimeout(tick, 50);
        };
        setTimeout(tick, 50);
    });
}

export async function waitForVisualsReady(selectors: Array<string | null>, timeoutMs = 8000): Promise<void> {
    const required = selectors.filter((sel): sel is string => !!sel);
    await Promise.all(required.map(sel => waitForElement(sel, timeoutMs)));
    await waitForPaint();
}

async function waitForOptionalSvg(containerSelector: string, timeoutMs = 2000): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        if (document.querySelector(`${containerSelector} svg`)) {
            await waitForPaint();
            return;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
    }
}

export async function deleteEmployeeReportImage(fileName, state, props) {
    var currentDateTime = state.currentDateTime;
    var display_id = props.UserState.report_display_id;

    let url = "/IRISReportsMicroservice/api/PDF/NewEmployeeReportPDF/delete_image";

    var obj = {};

    obj["display_id"] = display_id;
    obj["currentDateTime"] = currentDateTime;
    obj["imageName"] = fileName;
    obj["vintage"] = getVintageFromReportType("employee-report", props.UserState);
    var headers = authHeader();
    headers.append('Accept', 'application/json'); // This one is enough for GET requests
    headers.append('Content-Type', 'application/json'); // This one sends body
    const res = await fetch(url, { method: 'POST', body: JSON.stringify(obj), headers: headers });
    if (!res.ok) {
        throw new Error("delete_image failed");
    }
}

export async function captureEmployeeReportPdfImages({
    NewEmployeeReportState,
    report_display_id,
    currentDateTime,
    vintage,
    isNational,
    state,
    props
}): Promise<void> {
    const chart3Selector = isNational ? "#earnings-in-top-10-companies" : null;
    await waitForVisualsReady([
        "#employee-map #empMap path",
        "#chartEmployeeCounts",
        "#chartEarnings",
        chart3Selector
    ]);

    await Promise.all([
        waitForOptionalSvg("#chartEmployeeCounts"),
        waitForOptionalSvg("#chartEarnings"),
        isNational ? waitForOptionalSvg("#earnings-in-top-10-companies") : Promise.resolve()
    ]);

    const captures = [
        { selector: "#employee-map", fileName: "employee-map.png" },
        { selector: "#chartEmployeeCounts", fileName: "chart1.png" },
        { selector: "#chartEarnings", fileName: "chart2.png" }
    ];

    const includeChart3 = isNational && !!document.querySelector("#earnings-in-top-10-companies svg");
    if (includeChart3) {
        captures.push({ selector: "#earnings-in-top-10-companies", fileName: "chart3.png" });
    } else {
        try {
            await deleteEmployeeReportImage("chart3.png", state, props);
        } catch (error) {
            console.log(error);
        }
    }

    for (const capture of captures) {
        await exportEmployeeReportImage(
            capture.selector,
            capture.fileName,
            NewEmployeeReportState,
            report_display_id,
            currentDateTime,
            "no",
            vintage
        );
    }
}

export const exportEmployeeReportImage = (
    divName,
    imageName,
    NewEmployeeReportState,
    report_display_id,
    currentDateTime,
    exportYesNo,
    vintage
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const yello = document.querySelector(divName);
        if (!yello) {
            reject(new Error(`Element not found: ${divName}`));
            return;
        }

        html2canvas(yello as HTMLElement).then(canvas => {
            const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
                const byteCharacters = atob(b64Data);
                const byteArrays = [];

                for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                    const slice = byteCharacters.slice(offset, offset + sliceSize);

                    const byteNumbers = new Array(slice.length);
                    for (let i = 0; i < slice.length; i++) {
                        byteNumbers[i] = slice.charCodeAt(i);
                    }

                    const byteArray = new Uint8Array(byteNumbers);
                    byteArrays.push(byteArray);
                }

                const blob = new Blob(byteArrays, { type: contentType });
                return blob;
            }

            let dataURL = canvas.toDataURL("image/png");
            const blob = b64toBlob(dataURL.substring(22), "image/png");
            var university = NewEmployeeReportState.EmployeeProfileInfo.university;
            var start_year = NewEmployeeReportState.EmployeeMethodology.start_year;
            var end_year = NewEmployeeReportState.EmployeeMethodology.end_year;
            var employeeRelease = NewEmployeeReportState.employeeRelease;

            let form = new FormData();
            form.append('image', blob, imageName);
            form.append("display_id", report_display_id);
            form.append("currentDateTime", currentDateTime);
            form.append("vintage", vintage);

            form.append("university", university);
            form.append("start_year", start_year);
            form.append("end_year", end_year);
            form.append("employeeRelease", employeeRelease);

            var headers = authHeader();
            let url = "/IRISReportsMicroservice/api/PDF/NewEmployeeReportPDF/send_image";

            fetch(url, { method: 'POST', body: form, headers: headers }).then(res => {
                return res.blob();
            })
                .then(blob => {
                    if (exportYesNo == "yes")
                        saveAs(blob, imageName);
                    resolve();
                }).catch((error) => {
                    console.log(error);
                    reject(error);
                })
        }).catch(reject);
    });
}